/* Angular */
import { createReducer, on } from '@ngrx/store';

/* Functions */
import { updateEntityInArray } from 'src/app/features/shared/functions';

/* actions */
import * as ClientDataStoreActions from './client-data-store.actions';

import { Client } from 'src/app/features/shared/models';

/* State key */
export const featureKey = 'ClientDataStore';

/* State interface */
export interface ClientState {
  isLoading?: boolean;
  clients?: Client[];
  activeClient?: Client;
  countryOfCitizenship?: boolean;
  dateOfBirth?: Date;
  gender?: string;
  passportExpirationDate?: Date;
  passportNo?: string;
  loyaltyProgramNumber?: string;
  allergies?: string;
  medications?: string;
  otherGender?: string;
  otherDateOfBirth?: Date;
  otherPassportNo?: string;
  otherPassportExpirationDate?: Date;
  otherTcId?: string;
  kvkkVisibility?: boolean;
  lastName?: string;
  firstName?: string;
  otherName?: string;
  otherSurname?: string;
  step1ButtonClick?: string;
  status?: string;
}

/* Initial values */
export const initialState: ClientState = {
  isLoading: null,
  clients: null,
  activeClient: null,
  countryOfCitizenship: false,
  dateOfBirth: null,
  gender: null,
  passportExpirationDate: null,
  passportNo: null,
  loyaltyProgramNumber: null,
  allergies: null,
  medications: null,
  otherGender: null,
  otherDateOfBirth: null,
  otherPassportNo: null,
  otherPassportExpirationDate: null,
  otherTcId: null,
  kvkkVisibility: null,
  lastName: null,
  firstName: null,
  otherName: null,
  otherSurname: null,
  step1ButtonClick: null,
  status: null,
};

export const reducer = createReducer(
  initialState,
  on(ClientDataStoreActions.StatusUpdated, (state, action) => {
    return {
      ...state,
      ...action.payload,
    };
  }),

  on(ClientDataStoreActions.Step1ButtonClick, (state, action) => {
    return {
      ...state,
      ...action.payload,
    };
  }),

  on(ClientDataStoreActions.OtherSurnameUpdated, (state, action) => {
    return {
      ...state,
      ...action.payload,
    };
  }),

  on(ClientDataStoreActions.SurnameUpdated, (state, action) => {
    return {
      ...state,
      ...action.payload,
    };
  }),

  on(ClientDataStoreActions.OtherNameUpdated, (state, action) => {
    return {
      ...state,
      ...action.payload,
    };
  }),

  on(ClientDataStoreActions.NameUpdated, (state, action) => {
    return {
      ...state,
      ...action.payload,
    };
  }),

  on(ClientDataStoreActions.KvkkVisibilityUpdated, (state, action) => {
    return {
      ...state,
      ...action.payload,
    };
  }),

  on(ClientDataStoreActions.OtherTcIdUpdated, (state, action) => {
    return {
      ...state,
      ...action.payload,
    };
  }),

  on(ClientDataStoreActions.OtherPassportNoUpdated, (state, action) => {
    return {
      ...state,
      ...action.payload,
    };
  }),

  on(
    ClientDataStoreActions.OtherPassportExpirationDateUpdated,
    (state, action) => {
      return {
        ...state,
        ...action.payload,
      };
    },
  ),

  on(ClientDataStoreActions.OtherDateOfBirthUpdated, (state, action) => {
    return {
      ...state,
      ...action.payload,
    };
  }),

  on(ClientDataStoreActions.OtherGenderUpdated, (state, action) => {
    return {
      ...state,
      ...action.payload,
    };
  }),

  on(ClientDataStoreActions.MedicationsUpdated, (state, action) => {
    return {
      ...state,
      ...action.payload,
    };
  }),

  on(ClientDataStoreActions.AllergiesUpdated, (state, action) => {
    return {
      ...state,
      ...action.payload,
    };
  }),

  on(ClientDataStoreActions.LoyaltProgramNumberUpdated, (state, action) => {
    return {
      ...state,
      ...action.payload,
    };
  }),

  on(ClientDataStoreActions.PassportExpirationDateUpdated, (state, action) => {
    return {
      ...state,
      ...action.payload,
    };
  }),

  on(ClientDataStoreActions.PassportNoUpdated, (state, action) => {
    return {
      ...state,
      ...action.payload,
    };
  }),


  on(ClientDataStoreActions.DateOfBirthUpdated, (state, action) => {
    return {
      ...state,
      ...action.payload,
    };
  }),

  on(ClientDataStoreActions.GenderUpdated, (state, action) => {
    return {
      ...state,
      ...action.payload,
    };
  }),

  on(ClientDataStoreActions.IsTurkeyCitizenUpdated, (state, action) => {
    return {
      ...state,
      ...action.payload,
    };
  }),

  on(ClientDataStoreActions.GetClients, (state, action) => ({
    ...state,
    isLoading: true,
  })),

  on(ClientDataStoreActions.GetClientsSuccessful, (state, action) => ({
    ...state,
    clients: action.payload.clients,
    isLoading: false,
  })),

  on(ClientDataStoreActions.GetClientsFailure, (state, action) => ({
    ...state,
    isLoading: false,
  })),

  on(ClientDataStoreActions.GetClientById, (state, action) => ({
    ...state,
    isLoading: true,
  })),

  on(ClientDataStoreActions.GetClientByIdSuccessful, (state, action) => ({
    ...state,
    /* TODO: replace item by id from data array */
    isLoading: false,
  })),

  on(ClientDataStoreActions.GetClientByIdFailure, (state, action) => ({
    ...state,
    isLoading: false,
  })),

  on(
    ClientDataStoreActions.GetActiveClientWithRelationsById,
    (state, action) => ({
      ...state,
      isLoading: true,
    }),
  ),

  on(
    ClientDataStoreActions.GetActiveClientByIdWithRelationsSuccessful,
    (state, action) => ({
      ...state,
      activeClient: action.payload.activeClient,
      isLoading: false,
    }),
  ),

  on(
    ClientDataStoreActions.GetActiveClientByIdWithRelationsFailure,
    (state, action) => ({
      ...state,
      isLoading: false,
    }),
  ),

  on(ClientDataStoreActions.CreateClient, (state, action) => ({
    ...state,
    isLoading: true,
  })),

  on(ClientDataStoreActions.CreateClientSuccessful, (state, action) => ({
    ...state,
    clients: [...(state.clients ?? []), action.payload.client],
    isLoading: false,
  })),

  on(ClientDataStoreActions.CreateClientFailure, (state, action) => ({
    ...state,
    isLoading: false,
  })),

  on(ClientDataStoreActions.UpdateClient, (state, action) => ({
    ...state,
    isLoading: true,
  })),

  on(ClientDataStoreActions.UpdateClientSuccessful, (state, action) => ({
    ...state,
    clients: updateEntityInArray<Client>(
      action.payload.id,
      action.payload.data,
      state.clients ?? [],
    ),
    isLoading: false,
  })),

  on(ClientDataStoreActions.UpdateClientFailure, (state, action) => ({
    ...state,
    isLoading: false,
  })),

  on(ClientDataStoreActions.DeleteClient, (state, action) => ({
    ...state,
    isLoading: true,
  })),

  on(ClientDataStoreActions.DeleteClientSuccessful, (state, action) => ({
    ...state,
    clients:
      state.clients?.filter((item) => action.payload.id !== item.id) ?? [],
    isLoading: false,
  })),

  on(ClientDataStoreActions.DeleteClientFailure, (state, action) => ({
    ...state,
    isLoading: false,
  })),

  on(ClientDataStoreActions.ResetState, () => initialState),
);
